import React from 'react';
import { IconButton, Link, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { DeleteOutline, EditOutlined, ExpandMore, PhoneOutlined } from '@mui/icons-material';
import StatusChip from '../../common/StatusChip';
import type { AdmissionEnquiry } from '../../../types/admissions';
import { fmtClassName } from '../../../utils/pdf';
import { yearLabel } from '../../../utils/academicYear';
import { formatPhone } from '../form/admissionFormFields';
import dateTimeUtils from '../../../utils/dateTimeUtils';

interface Props {
  enquiry: AdmissionEnquiry;
  canWrite: boolean;
  onEdit: (e: AdmissionEnquiry) => void;
  onRemove: (e: AdmissionEnquiry) => void;
  onStatusClick: (anchor: HTMLElement, e: AdmissionEnquiry) => void;
}

const fmt = (iso: string) => dateTimeUtils.formatDateForDisplay(iso);

/** One family on the enquiries list. */
const EnquiryRow: React.FC<Props> = ({ enquiry: e, canWrite, onEdit, onRemove, onStatusClick }) => (
  <TableRow hover sx={{ '& td': { py: 1.5 } }}>
    <TableCell>
      <Typography variant="body2" fontWeight={600}>{e.childName}</Typography>
      {e.dob && <Typography variant="caption" color="text.secondary">Born {fmt(e.dob)}</Typography>}
    </TableCell>
    <TableCell>
      <Typography variant="body2" fontWeight={500}>{fmtClassName(e.classId?.name ?? '')}</Typography>
      <Typography variant="caption" color="text.secondary">{yearLabel(e.forYear)}</Typography>
    </TableCell>
    <TableCell>
      <Typography variant="body2">{e.parentName}</Typography>
      <Link
        href={`tel:${e.parentPhone}`} underline="hover" variant="caption"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontVariantNumeric: 'tabular-nums' }}
      >
        <PhoneOutlined sx={{ fontSize: 13 }} />{formatPhone(e.parentPhone)}
      </Link>
    </TableCell>
    <TableCell>
      {e.note ? (
        <Tooltip title={e.note} placement="top-start">
          <Typography variant="body2" color="text.secondary" noWrap>{e.note}</Typography>
        </Tooltip>
      ) : <Typography variant="body2" color="text.disabled">—</Typography>}
    </TableCell>
    <TableCell>
      <Stack spacing={0.5} alignItems="flex-start">
        <StatusChip
          status={{ kind: 'enquiry', value: e.status }}
          {...(canWrite ? {
            onClick: (ev: React.MouseEvent<HTMLElement>) => onStatusClick(ev.currentTarget, e),
            onDelete: (ev: React.MouseEvent<HTMLElement>) => onStatusClick((ev.currentTarget as HTMLElement).closest('.MuiChip-root') as HTMLElement, e),
            deleteIcon: <ExpandMore />,
          } : {})}
        />
        {e.status === 'called' && e.calledAt && (
          <Typography variant="caption" color="text.secondary">on {fmt(e.calledAt)}</Typography>
        )}
      </Stack>
    </TableCell>
    <TableCell>
      <Typography variant="body2" color="text.secondary" noWrap>{fmt(e.createdAt)}</Typography>
    </TableCell>
    <TableCell align="right">
      {canWrite && (
        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
          <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(e)} aria-label={`Edit ${e.childName}`}><EditOutlined fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Remove"><IconButton size="small" onClick={() => onRemove(e)} aria-label={`Remove ${e.childName}`}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
        </Stack>
      )}
    </TableCell>
  </TableRow>
);

export default React.memo(EnquiryRow);
